/**
 * BlacklistModal Component
 *
 * Modal for blacklisting a contact or company.
 * Requires a reason that will be visible to AI agents.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BlacklistModalState } from '../types';

interface BlacklistModalProps {
  state: BlacklistModalState;
  onConfirm: (reason: string) => Promise<void>;
  onCancel: () => void;
}

export default function BlacklistModal({
  state,
  onConfirm,
  onCancel,
}: BlacklistModalProps) {
  const { t } = useTranslation('people');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!state.isOpen) return null;

  const handleConfirm = async () => {
    if (reason.trim().length < 10) return;

    setIsSubmitting(true);
    try {
      await onConfirm(reason);
      setReason('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-red-50 to-orange-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('blacklist_modal.title', { entityType: state.entityType === 'contact' ? t('blacklist_modal.contact') : t('blacklist_modal.company') })}
              </h3>
              <p className="text-sm text-gray-500">
                {state.entityName}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Warning */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="text-sm text-amber-800">
                <p className="font-medium">{t('blacklist_modal.warning_title')}</p>
                <p className="mt-1 text-amber-700">
                  {t('blacklist_modal.warning_description', { entityType: state.entityType })}
                </p>
              </div>
            </div>
          </div>

          {/* Reason input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('blacklist_modal.reason_label')} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('blacklist_modal.reason_placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-200 focus:border-red-400 transition-colors resize-none"
              rows={3}
              autoFocus
            />
            <p className="mt-1.5 text-xs text-gray-500">
              {t('blacklist_modal.min_chars_note')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {t('blacklist_modal.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSubmitting || reason.trim().length < 10}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t('blacklist_modal.processing') : t('blacklist_modal.blacklist')}
          </button>
        </div>
      </div>
    </div>
  );
}
